import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
    DELIVERY_BLOCKED_CODE,
    DELIVERY_BLOCKED_MESSAGE,
    SecurityService,
} from './security.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    private static readonly HEADER_SKIP_REFRESH = 'X-Skip-Auth-Refresh';
    private static readonly HEADER_RETRY = 'X-Auth-Retry';

    constructor(private security: SecurityService) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const isApiUrl = req.url.startsWith(environment.apiBaseUrl);
        if (!isApiUrl || this.isPublicAuthRequest(req)) {
            const passthroughRequest = this.stripInternalHeaders(req);
            return next.handle(passthroughRequest).pipe(
                catchError((error: HttpErrorResponse) => {
                    if (this.isDeliveryBlockedError(error)) {
                        this.security.logout({
                            code: DELIVERY_BLOCKED_CODE,
                            message: error?.error?.message || DELIVERY_BLOCKED_MESSAGE,
                        });
                    }

                    return throwError(() => error);
                })
            );
        }

        return this.security.getValidAccessTokenForRequest$().pipe(
            switchMap((token) => {
                const requestWithAuth = token ? this.addAuthHeader(req, token) : this.stripInternalHeaders(req);
                return next.handle(requestWithAuth).pipe(
                    catchError((error: HttpErrorResponse) => this.handleApiError(error, requestWithAuth, next))
                );
            })
        );
    }

    private handleApiError(
        error: HttpErrorResponse,
        request: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        if (this.isDeliveryBlockedError(error)) {
            this.security.logout({
                code: DELIVERY_BLOCKED_CODE,
                message: error?.error?.message || DELIVERY_BLOCKED_MESSAGE,
            });

            return throwError(() => error);
        }

        if (!this.shouldTryRefresh(error, request)) {
            return throwError(() => error);
        }

        return this.security.refreshAccessToken$('401').pipe(
            switchMap((refreshedToken) => {
                const retriedRequest = this.markAsRetried(this.addAuthHeader(request, refreshedToken));
                return next.handle(retriedRequest);
            }),
            catchError((refreshError) => {
                if (this.isTransientNetworkError(refreshError)) {
                    return throwError(() => error);
                }

                return throwError(() => refreshError);
            })
        );
    }

    private shouldTryRefresh(error: HttpErrorResponse, request: HttpRequest<any>): boolean {
        const isUnauthorized = error?.status === 401;
        const alreadyRetried = request.headers.get(TokenInterceptor.HEADER_RETRY) === '1';

        return isUnauthorized && !alreadyRetried && !!this.security.getRefreshToken();
    }

    private isPublicAuthRequest(request: HttpRequest<any>): boolean {
        if (request.headers.get(TokenInterceptor.HEADER_SKIP_REFRESH) === 'true') {
            return true;
        }

        const loginUrl = `${environment.apiBaseUrl}/api/Delivery/Login`;
        const refreshUrl = `${environment.apiBaseUrl}${environment.auth.refreshEndpoint}`;

        return request.url.startsWith(loginUrl) || request.url.startsWith(refreshUrl);
    }

    private stripInternalHeaders(request: HttpRequest<any>): HttpRequest<any> {
        if (!request.headers.has(TokenInterceptor.HEADER_SKIP_REFRESH) && !request.headers.has(TokenInterceptor.HEADER_RETRY)) {
            return request;
        }

        let headers = request.headers;
        headers = headers.delete(TokenInterceptor.HEADER_SKIP_REFRESH);
        headers = headers.delete(TokenInterceptor.HEADER_RETRY);

        return request.clone({ headers });
    }

    private addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
        const sanitized = this.stripInternalHeaders(request);
        return sanitized.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    private markAsRetried(request: HttpRequest<any>): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                [TokenInterceptor.HEADER_RETRY]: '1',
            },
        });
    }

    private isTransientNetworkError(error: any): boolean {
        if (error instanceof TimeoutError) {
            return true;
        }
        return error?.status === 0;
    }

    private isDeliveryBlockedError(error: HttpErrorResponse): boolean {
        return error?.status === 401 && error?.error?.code === DELIVERY_BLOCKED_CODE;
    }
}
